import React, { useState, useEffect, useRef } from 'react';
import { BellIcon, XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useUser } from '../contexts/UserContext';
import { WORK_ORDER_STATUS } from '../constants/statusEnums';
import notificationsService from '../services/NotificationsService';
import { useNavigate } from 'react-router-dom';
import { isAdmin } from '../utils/roleUtils';
import { getSupabaseClient } from '../utils/supabaseClient';


import { companyDateKey } from '../utils/dateFormatters';
const POLL_MS = 60000; // 60s

const NotificationCenter = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filterMode, setFilterMode] = useState('all'); // all | unread | 24h | 7d
  const pollRef = useRef(null);
  const realtimeRef = useRef(false);
  const containerRef = useRef(null);

  // Close the panel on outside-click or Escape so it doesn't linger behind
  // other top-bar popovers (the "New" and profile menus).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    const handleKeyDown = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const load = async () => {
    if (!user?.company_id) return;
    try {
      // The list and the count are independent, so they are asked for together (page speed).
      const [data, count] = await Promise.all([
        notificationsService.getNotifications(user.company_id, user?.id, { limit: 20 }),
        notificationsService.getUnreadCount(user.company_id, user?.id),
      ]);
      setNotifications(data.map(n => ({
        ...n,
        read: n.status === 'read',
        is_read: n.status === 'read',
        timestamp: n.created_at
      })));
      setUnreadCount(count);
    } catch (e) {
      console.warn('Notifications load failed', e);
    }
  };

  const openNotification = async (n) => {
    try {
      // Mark as read optimistically
      if (!n.is_read && !n.read) {
        await notificationsService.markAsRead(n.id);
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true, is_read: true } : x));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {}

    // Use action_url if available (new standard)
    // Fix legacy /quotes/{id} URLs and route approved quotes to jobs
    if (n.action_url) {
      const quoteMatch = n.action_url.match(/^\/quotes(?:\?open=|\/)?([0-9a-f-]{36})$/);
      const isApproved = (n.title || '').toLowerCase().includes('approved') || (n.data?.new_status === WORK_ORDER_STATUS.APPROVED);
      if (quoteMatch && isApproved) {
        navigate(`/jobs?edit=${quoteMatch[1]}&schedule=new`);
      } else if (quoteMatch) {
        navigate(`/quotes?open=${quoteMatch[1]}`);
      } else {
        navigate(n.action_url);
      }
      return;
    }

    // Route based on notification type using switch/case
    // Extract safe values with fallbacks
    const d = n.data || {};
    const category = String(d.category || n.type || '').toUpperCase();
    const relatedId = d.related_id || n.related_id || d.work_order_id || d.invoice_id || d.customer_id || d.id || null;
    
    // Heuristic: detect quote approval notifications without explicit category
    const titleLower = (n.title || '').toLowerCase();
    const messageLower = (n.message || '').toLowerCase();
    const looksLikeQuoteApproval = (titleLower.includes('quote') || messageLower.includes('quote')) && (titleLower.includes('approved') || messageLower.includes('approved')) && (d.work_order_id || relatedId);

    // Use switch/case for cleaner routing based on category/type
    switch (category) {
      case 'WORK_ORDER':
      case 'JOB':
        // QUOTE APPROVED -> go schedule the job
        if (looksLikeQuoteApproval || d.new_status === WORK_ORDER_STATUS.APPROVED) {
          const jobId = d.work_order_id || relatedId;
          if (jobId) {
            navigate(`/jobs?edit=${jobId}&schedule=new`);
            return;
          }
        }
        // Regular work order - go to jobs
        if (relatedId) {
          navigate(`/jobs?edit=${relatedId}`);
        } else {
          navigate('/jobs');
        }
        break;

      case 'DISPUTE':
      case 'DISPUTE_RESOLVED':
        navigate('/disputes');
        break;

      case 'QUOTE':
        // QUOTE-related navigation
        if (relatedId) {
          navigate(`/quotes?open=${relatedId}`);
        } else if (d.work_order_id) {
          navigate(`/quotes?open=${d.work_order_id}`);
        } else {
          navigate('/quotes');
        }
        break;

      case 'MESSAGE':
        // MESSAGE-related - go to Messages hub
        navigate('/messages');
        break;

      case 'INVENTORY':
        // INVENTORY navigation
        navigate(relatedId ? `/operations/inventory?id=${relatedId}` : '/operations/inventory');
        break;

      case 'PTO':
        // PTO / Time Off - route based on admin status
        // Board #456. This sent owners to /admin/time-off, which renders
        // <Timesheets initialTab="accruals" /> — and Timesheets has no accruals tab, so it
        // fell through to the ordinary timesheet table with nothing selected. The
        // notification had no usable destination, and the one it aimed at reinforced a
        // payroll/accrual workflow the product has explicitly rejected (migration 20260824a).
        // The real company-wide surface is the Time Off tab on Team > Employees.
        // 2026-09-24: carry the entry id so the Time Off tab highlights it, and send
        // non-admins to /my/time-off - the registered route. '/my-time-off' matched nothing.
        navigate(isAdmin(user)
          ? `/employees?tab=time-off${relatedId ? `&id=${encodeURIComponent(relatedId)}` : ''}`
          : '/my/time-off');
        break;

      case 'SCHEDULE':
      case 'CALENDAR':
        // SCHEDULE / Calendar navigation
        if (d.start_time) {
          // The job's day on the company calendar (the Calendar page buckets by companyDateKey).
          const date = companyDateKey(d.start_time);
          navigate(`/calendar?date=${date}`);
        } else {
          navigate('/calendar');
        }
        break;

      case 'INVOICE':
      case 'INVOICE_OVERDUE':
        // Invoice navigation
        navigate('/invoices');
        break;

      case 'QUOTE_APPROVED':
        // Quote approved - go to jobs page to schedule the job
        if (relatedId) {
          navigate(`/jobs?edit=${relatedId}&schedule=new`);
        } else if (d.work_order_id) {
          navigate(`/jobs?edit=${d.work_order_id}&schedule=new`);
        } else {
          navigate('/jobs');
        }
        break;

      case 'QUOTE_REJECTED':
        // Quote rejected - go to quotes list
        if (relatedId) {
          navigate(`/quotes?open=${relatedId}`);
        } else {
          navigate('/quotes?filter=rejected');
        }
        break;

      case 'QUOTE_CHANGES_REQUESTED':
        // Changes requested - go to quotes to see customer message
        if (relatedId) {
          navigate(`/quotes?open=${relatedId}`);
        } else {
          navigate('/quotes?filter=changes_requested');
        }
        break;

      case 'SYSTEM':
        // System notifications - check if it's message-related
        if (titleLower.includes('message') || messageLower.includes('message')) {
          navigate('/messages');
        } else {
          navigate('/notifications');
        }
        break;

      default:
        // Fallback for unknown types - check common patterns
        if (titleLower.includes('rejected') || titleLower.includes('declined') || category.includes('REJECTED')) {
          navigate('/quotes?filter=rejected');
        } else if (titleLower.includes('changes') || titleLower.includes('requested') || category.includes('CHANGES')) {
          navigate('/quotes?filter=changes_requested');
        } else if (titleLower.includes('invoice') || category.includes('INVOICE')) {
          navigate('/invoices');
        } else if (titleLower.includes('message')) {
          navigate('/messages');
        } else {
          // Default fallthrough to notifications page
          navigate('/notifications');
        }
        break;
    }
  };

  useEffect(() => {
    load();
    // Only start polling if Realtime isn't connected yet
    if (!realtimeRef.current) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(load, POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [user?.company_id]);

  // Realtime: instant bell updates when new notifications arrive
  useEffect(() => {
    if (!user?.company_id) return;
    const supabase = getSupabaseClient();

    const onInsert = (payload) => {
      try {
        const n = payload.new || {};
        // Only count unread/pending items for this user or broadcast (null user)
        const isForUser = !n.user_id || n.user_id === user.id;
        const isPending = (n.status || 'pending') === 'pending';
        if (!isForUser || !isPending) return;

        setUnreadCount((prev) => prev + 1);
        setNotifications((prev) => [
          { ...n, read: false, is_read: false, timestamp: n.created_at },
          ...prev
        ].slice(0, 50));
      } catch (e) {
        // no-op
      }
    };

    const channel = supabase
      .channel(`notifications_${user?.id || 'company'}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `company_id=eq.${user.company_id}`
        },
        (payload) => onInsert(payload)
      );
    // A row addressed to this user can carry company_id NULL (dispute notifications do), so the
    // company filter above never delivers it. Rows that DO carry this company arrive above.
    if (user?.id) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if ((payload.new || {}).company_id === user.company_id) return;
          onInsert(payload);
        }
      );
    }
    // removeChannel() in the cleanup reports CLOSED to this callback, which used to start a
    // 60 s poll AFTER unmount/logout that nothing ever stopped.
    let disposed = false;
    channel
      .subscribe((status) => {
        if (disposed) return;
        // Kill polling when Realtime connects
        if (status === 'SUBSCRIBED') {
          realtimeRef.current = true;
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } else {
          // Realtime disconnected, restart polling
          realtimeRef.current = false;
          if (!pollRef.current) {
            pollRef.current = setInterval(load, POLL_MS);
          }
        }
      });

    return () => {
      disposed = true;
      try { supabase.removeChannel(channel); } catch {}
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [user?.company_id, user?.id]);

  const markAsRead = async (notificationId) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.warn('markAsRead failed', e); }
  };

  const markAllAsRead = async () => {
    try {
      // Mark all company notifications as read (broadcast + user-specific)
      await notificationsService.markAllAsRead(user.company_id, user.id);
      setNotifications(prev => prev.map(n => ({ ...n, read: true, is_read: true })));
      setUnreadCount(0);
    } catch (e) { console.warn('markAllAsRead failed', e); }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationsService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      const n = notifications.find(x => x.id === notificationId);
      if (n && !n.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.warn('deleteNotification failed', e); }
  };

  const getNotificationIcon = (type, severity) => {
    const base = "h-8 w-8 rounded-full p-1.5 flex items-center justify-center";
    // Severity is stored in data JSON column, not as top-level column
    const sev = (severity || 'INFO').toUpperCase();
    const sevClass = sev === 'ERROR' ? 'bg-rose-100 text-rose-600' : sev === 'WARNING' ? 'bg-amber-100 text-amber-600' : sev === 'SUCCESS' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600';
    const wrap = (icon) => <div className={`${base} ${sevClass}`}>{icon}</div>;
    if (type === 'INVENTORY') return wrap(<InformationCircleIcon className="h-5 w-5" />);
    if (type === 'PTO') return wrap(<CalendarDaysIcon className="h-5 w-5" />);
    if (type === 'INVOICE') return wrap(<InformationCircleIcon className="h-5 w-5" />);
    if (type === 'QUOTE') return wrap(<InformationCircleIcon className="h-5 w-5" />);
    if (sev === 'ERROR') return wrap(<XMarkIcon className="h-5 w-5" />);
    if (sev === 'WARNING') return wrap(<ExclamationTriangleIcon className="h-5 w-5" />);
    if (sev === 'SUCCESS') return wrap(<CheckCircleIcon className="h-5 w-5" />);
    return wrap(<InformationCircleIcon className="h-5 w-5" />);
  };

  // Helper to get severity from notification (severity is in data JSON)
  const getNotificationSeverity = (notification) => {
    return notification.data?.severity || notification.severity || 'INFO';
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
        aria-label="Open notifications"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto top-14 sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-96 max-w-[calc(100vw-1rem)] bg-white dark:bg-slate-900 rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50 z-50">
          {/* Header. Eight controls — the heading, four range chips, Mark all read and the
              close button — were competing for one 384px row, so they overlapped each other
              and, through a translucent panel, the dashboard toggle behind it. Title and the
              two actions take the first row; the range chips get their own beneath, where
              they have the width to sit side by side. The panel is opaque for the same
              reason: a see-through dropdown mixes its text with whatever it covers. */}
          <div className="p-4 border-b border-blue-200/30 dark:border-blue-800/30">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h3>
              <div className="flex shrink-0 items-center gap-3">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium whitespace-nowrap"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close notifications"
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1">
              {[
                { label: 'All', value: 'all' },
                { label: 'Unread', value: 'unread' },
                { label: '24h', value: '24h' },
                { label: '7d', value: '7d' }
              ].map(f => (
                <button
                  key={f.value}
                  onClick={() => setFilterMode(f.value)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                    filterMode === f.value
                      ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-400/50 shadow-sm'
                      : 'bg-white/40 dark:bg-slate-800/40 text-gray-700 dark:text-gray-300 border-gray-300/30 dark:border-gray-600/30 hover:bg-white/60 dark:hover:bg-slate-700/60'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {(
              (() => {
                const now = new Date();
                const filtered = notifications.filter(n => {
                  if (filterMode === 'unread') return !n.read;
                  if (filterMode === '24h') return (now - new Date(n.timestamp)) <= 24*60*60*1000;
                  if (filterMode === '7d') return (now - new Date(n.timestamp)) <= 7*24*60*60*1000;
                  return true;
                });
                return filtered;
              })()
            ).length > 0 ? (
              (
                (() => {
                  const now = new Date();
                  return notifications.filter(n => {
                    if (filterMode === 'unread') return !n.read;
                    if (filterMode === '24h') return (now - new Date(n.timestamp)) <= 24*60*60*1000;
                    if (filterMode === '7d') return (now - new Date(n.timestamp)) <= 7*24*60*60*1000;
                    return true;
                  });
                })()
              ).map((n) => (
                // NOT role="button" ANY MORE. This row declared itself a button and then
                // contained two real ones -- the delete X and "Mark as read" -- which is the
                // nested-interactive violation the crawler reported 40 times (once per rendered
                // notification; it is one component, not forty defects). A control inside a
                // control is genuinely broken rather than untidy: assistive tech flattens the
                // outer button's label, so the inner buttons lose their own names and the row
                // reads as one long string.
                //
                // The div keeps its onClick so the whole row stays clickable with a mouse -- a
                // plain div with a handler is not an interactive ARIA node, so it cannot nest --
                // and the title block below is now a real button, which is what keyboard and
                // screen-reader users activate. Same behaviour for everyone, one control each.
                <div
                  key={n.id}
                  onClick={() => { setIsOpen(false); openNotification(n); }}
                  className={`w-full text-left p-4 border-b border-blue-200/20 dark:border-blue-800/20 transition-all cursor-pointer ${
                    !n.read
                      ? 'bg-blue-500/10 hover:bg-blue-500/20'
                      : 'hover:bg-white/30 dark:hover:bg-slate-800/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getNotificationIcon(n.type, getNotificationSeverity(n))}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        {/* min-w-0 and break-words are both load-bearing. A flex child's
                            default min-width is auto, so it refuses to shrink below its
                            content and a long unbroken token pushes the row past the panel
                            instead of wrapping inside it. The dropdown is a PREVIEW, so the
                            message is clamped to three lines — one live notification on
                            48f32d34 carries 4,053 characters (2026-09-17), which rendered in
                            full and buried every row beneath it. The whole text stays
                            readable on the Notifications page. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setIsOpen(false); openNotification(n); }}
                          className="flex-1 min-w-0 text-left"
                        >
                          <p className={`text-sm font-semibold break-words ${!n.read ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>{n.title}</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 break-words line-clamp-3">{n.message}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">{formatTimestamp(n.timestamp)}</p>
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }} aria-label={`Delete notification: ${n.title}`} className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 ml-2 transition-colors">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                      {!n.read && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mt-2 font-medium">
                          Mark as read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-600 dark:text-gray-400">
                <BellIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <p className="font-medium">No notifications yet</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-blue-200/30 dark:border-blue-800/30 text-center bg-white/20 dark:bg-slate-900/20 rounded-b-lg">
            <button
              onClick={() => { setIsOpen(false); navigate('/notifications'); }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;

